// Slice H2 — Bollinger Band Mean-Reversion (curated template v1).
//
// Week 2 A2 rewrite — proper Bollinger band exposure via CUSTOM blocks.
//
// Background.
//   The Week 2 schema-fix pass got all the field naming right (SL/TP/
//   entry/risk_rule canonical shapes) but left the strategy logic
//   degraded: the engine's built-in `volatility` block exposes only the
//   middle band, so the Bollinger fade entry was running as
//   "close < midline" (a coarse proxy) instead of the documented
//   "close < lower band" (true oversold).
//
//   A2 fixes this at the TEMPLATE layer (no engine change required) by
//   replacing the `volatility` block with TWO CUSTOM `indicator` blocks
//   that call `helpers.bollingerBands(close, period, mult)` directly
//   and expose the lower / middle bands as their respective output
//   series. Expressions then read the correct band via standard
//   `leftSource: 'input'` wiring.
//
// Strategy logic (Week 2 A2 — now actually matches the documentation):
//   • Bollinger Bands (20, 2σ) on close.
//   • Enter long when close < LOWER band AND RSI(14) < 30 (true
//     oversold confluence).
//   • Exit at the MIDDLE band (mean reversion target).
//   • Long-only v1 (symmetric short deferred).
//   • 1.5% percentage stop, 1:2 risk-reward TP.
//   • Per-trade risk capped at 1% via canonical max_risk_per_trade_pct.

import { BlockType, type Block } from '../types';
import {
  type CuratedTemplate,
  templateBlockMetadata,
  namespacedBlockId,
} from './types';
import {
  maxRiskPerTradeParameters,
} from './canonicalRiskRuleBodies';
import {
  CANONICAL_BB_LOWER_TSL,
  CANONICAL_BB_MIDDLE_TSL,
} from './canonicalIndicatorBands';

const SLUG = 'mr-bollinger-fade';
const id = (name: string) => namespacedBlockId(SLUG, name);
const meta = (desc: string) => templateBlockMetadata(desc);

const blocks: Block[] = [
  {
    id: id('config'),
    type: BlockType.STRATEGY_CONFIG,
    data: {
      name: 'Strategy config',
      initial_capital: 10_000,
      commission_value: 0.06,
      commission_type: 'percentage',
      slippage_value: 0.02,
      slippage_type: 'percentage',
      // S65 (2026-06-06) — normalized to false. See resolveEffectiveConfig.ts
      // for the rationale + trade-off; this matches the new product-wide
      // default. Users can tick it back on in the cfg block.
      process_orders_on_close: false,
    } as Block['data'],
    position: { x: 50, y: 320 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Capital + costs sized for crypto spot 15m-1h.'),
  },

  {
    id: id('price'),
    type: BlockType.PRICE_DATA,
    data: { name: 'Price data' } as Block['data'],
    position: { x: 50, y: 80 },
    connections: { inputs: [], outputs: [id('rsi')] },
    metadata: meta('Source candle feed.'),
  },

  // ── Bollinger LOWER band via CUSTOM indicator block (A2 fix) ─────
  // Calls helpers.bollingerBands(close, 20, 2).lower internally and
  // exposes the lower-band series as the block's output. Expressions
  // read it via standard `leftSource: 'input'` wiring.
  {
    id: id('bb-lower'),
    type: BlockType.CUSTOM,
    data: {
      name: 'Bollinger LOWER band (20, 2σ)',
      blockType: 'indicator',
      tslAst: CANONICAL_BB_LOWER_TSL,
      parameters: { period: 20, stdDev: 2, basis: 'sma' },
      // S4d (2026-06-02): `outputTypes` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 230, y: 40 },
    connections: { inputs: [], outputs: [id('expr-close-below-lower')] },
    metadata: meta('CUSTOM indicator — true lower band, not midline proxy.'),
  },

  // ── Bollinger MIDDLE band — exit target ──────────────────────────
  {
    id: id('bb-middle'),
    type: BlockType.CUSTOM,
    data: {
      name: 'Bollinger MIDDLE band (20)',
      blockType: 'indicator',
      tslAst: CANONICAL_BB_MIDDLE_TSL,
      parameters: { period: 20, stdDev: 2, basis: 'sma' },
      // S4d (2026-06-02): `outputTypes` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 230, y: 100 },
    connections: { inputs: [], outputs: [id('exit-on-middle')] },
    metadata: meta('CUSTOM indicator — middle (20-bar SMA) for the exit target.'),
  },

  {
    id: id('rsi'),
    type: BlockType.MOMENTUM_OSCILLATOR,
    data: {
      name: 'RSI (14)',
      indicator: 'rsi',
      period: 14,
      source: 'close',
    } as Block['data'],
    position: { x: 230, y: 160 },
    connections: { inputs: [id('price')], outputs: [id('expr-rsi-oversold')] },
    metadata: meta('Confirms oversold — pairs with the lower-band breach.'),
  },

  // ── Entry conditions ─────────────────────────────────────────────
  // A2 fix: this expression now references the bb-lower CUSTOM block,
  // so close is genuinely compared against the lower Bollinger band —
  // not the midline proxy from the v1 release.
  {
    id: id('expr-close-below-lower'),
    type: BlockType.EXPRESSION,
    data: {
      name: 'Close < Lower Band (true oversold)',
      leftSource: 'price',
      leftPriceSource: 'close',
      leftPriceDataBlockId: id('price'),
      rightSource: 'input',
      rightInputBlockId: id('bb-lower'),
      operator: '<',
    } as Block['data'],
    position: { x: 430, y: 60 },
    connections: { inputs: [id('bb-lower')], outputs: [id('and-entry')] },
    metadata: meta('Close below the genuine lower band.'),
  },

  {
    id: id('expr-rsi-oversold'),
    type: BlockType.EXPRESSION,
    data: {
      name: 'RSI < 30 (oversold)',
      leftSource: 'input',
      leftInputBlockId: id('rsi'),
      rightSource: 'static',
      rightValue: 30,
      operator: '<',
    } as Block['data'],
    position: { x: 430, y: 140 },
    connections: { inputs: [id('rsi')], outputs: [id('and-entry')] },
    metadata: meta('RSI below 30 confirms oversold (not noise).'),
  },

  {
    id: id('and-entry'),
    type: BlockType.LOGIC_GATE,
    data: {
      name: 'Entry AND',
      gateType: 'AND',
    } as Block['data'],
    position: { x: 630, y: 100 },
    connections: {
      inputs: [id('expr-close-below-lower'), id('expr-rsi-oversold')],
      outputs: [id('entry')],
    },
    metadata: meta('Both signals must agree.'),
  },

  // ── Terminals ────────────────────────────────────────────────────
  {
    id: id('entry'),
    type: BlockType.ENTRY,
    data: {
      name: 'Long entry (fade)',
      direction: 'long',
      entryType: 'market',
      logic: 'AND',
      useStrategyDefaults: false,
    } as Block['data'],
    position: { x: 830, y: 100 },
    connections: { inputs: [id('and-entry')], outputs: [] },
    metadata: meta('Market long; canonical risk rule resizes against the SL.'),
  },

  {
    id: id('exit-on-middle'),
    type: BlockType.EXIT,
    data: {
      name: 'Exit at middle band',
    } as Block['data'],
    position: { x: 830, y: 200 },
    connections: { inputs: [id('bb-middle')], outputs: [] },
    metadata: meta('Mean-reversion target = the 20-bar SMA (middle band).'),
  },

  {
    id: id('sl'),
    type: BlockType.STOP_LOSS,
    data: {
      name: 'Stop loss (1.5%)',
      stopType: 'percentage',
      stopValue: 1.5,
      // S4c (2026-06-02): `executionMethod` removed (dead field). See audit doc.
      exitMode: 'full',
    } as Block['data'],
    position: { x: 830, y: 280 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Tight — mean-reversion edges are quick.'),
  },

  {
    id: id('tp'),
    type: BlockType.TAKE_PROFIT,
    data: {
      name: 'Take profit (1:2 risk-reward)',
      tpType: 'risk_reward',
      riskRewardRatio: 2.0,
      // S4c (2026-06-02): `executionMethod` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 830, y: 360 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('1:2 ratio — adjust if the middle-band exit fires consistently.'),
  },

  // ── Risk control ─────────────────────────────────────────────────
  {
    id: id('risk-max-per-trade'),
    type: BlockType.CUSTOM,
    data: {
      name: 'Max risk per trade (1%)',
      blockType: 'risk_rule',
      parameters: maxRiskPerTradeParameters(1.0, 'resize'),
      // S4d (2026-06-02): `outputTypes` removed (dead field). See audit doc.
    } as Block['data'],
    position: { x: 50, y: 480 },
    connections: { inputs: [], outputs: [] },
    metadata: meta('Per-trade risk capped at 1% of equity (canonical).'),
  },
];

export const MR_BOLLINGER_FADE: CuratedTemplate = {
  slug: SLUG,
  name: 'Bollinger Band Mean-Reversion (v1)',
  family: 'mean-reversion',
  bestFor: 'Fades oversold extremes (close below LOWER Bollinger band + RSI < 30) back to the mean.',
  detail:
    'Classic two-signal mean-reversion: requires BOTH a Bollinger LOWER-band breach AND ' +
    'RSI < 30 before entering long. Exits at the MIDDLE band (the 20-bar SMA). Stops are tight ' +
    '(1.5%) because mean-reversion edges are short-duration; a stop-out usually means the ' +
    'reversion never started. Long-only v1 — symmetric short logic deferred to v2.',
  assetGuidance:
    'Range-bound spot pairs work best (ETH/USDT, mid-cap alts). Avoid heavy-trending assets ' +
    'or trending periods of normally-ranging assets.',
  timeframeGuidance: ['15m', '30m', '1h'],
  riskNotes: [
    'Trending markets will produce serial stop-outs',
    'Tight stop is fragile to slippage spikes',
    'Long-only — symmetric shorting deferred',
  ],
  expectedValidation:
    'Yellow common; red+tail_fail if the holdout window happened to be a trending period; ' +
    'insufficient OOS trades possible on short backtests if the RSI/band confluence is rare.',
  blocks,
  version: 'v1',
};
